import React, { useEffect, useState } from 'react';
import { Image, MapPin, Ruler, Table, X } from 'lucide-react';
import { VisualElements } from '../../types';

interface DiagramCardProps {
  imageUrl?: string;
  extractedText?: string;
  visualElements: VisualElements;
}

export const DiagramCard: React.FC<DiagramCardProps> = ({
  imageUrl,
  extractedText,
  visualElements,
}) => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const angles = visualElements.measurements?.angles || [];
  const lengths = visualElements.measurements?.lengths || [];
  const areas = visualElements.measurements?.areas || [];
  const other = visualElements.measurements?.other || [];
  const points = visualElements.coordinates || [];
  const markings = visualElements.specialMarkings || [];

  const visiblePoints = points.slice(0, 6);
  const hiddenPointsCount = Math.max(0, points.length - visiblePoints.length);

  const visibleAngles = angles.slice(0, 4);
  const hiddenAnglesCount = Math.max(0, angles.length - visibleAngles.length);

  const visibleLengths = lengths.slice(0, 4);
  const hiddenLengthsCount = Math.max(0, lengths.length - visibleLengths.length);

  const visibleAreas = areas.slice(0, 2);
  const hiddenAreasCount = Math.max(0, areas.length - visibleAreas.length);

  const visibleOther = other.slice(0, 2);
  const hiddenOtherCount = Math.max(0, other.length - visibleOther.length);

  const visibleMarkings = markings.slice(0, 2);
  const hiddenMarkingsCount = Math.max(0, markings.length - visibleMarkings.length);

  const hasRequiredInfo =
    points.length > 0 ||
    angles.length > 0 ||
    lengths.length > 0 ||
    areas.length > 0 ||
    other.length > 0 ||
    markings.length > 0;

  const hasDetails =
    visualElements.hasTable ||
    visualElements.hasGraph ||
    !!extractedText ||
    !!visualElements.diagramDescription ||
    points.length > 0 ||
    angles.length > 0 ||
    lengths.length > 0 ||
    areas.length > 0 ||
    other.length > 0 ||
    markings.length > 0;

  useEffect(() => {
    if (!isDetailsOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsDetailsOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isDetailsOpen]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
      {/* Header */}
      <div className="px-3 py-2 bg-blue-50 border-b border-blue-100">
        <div className="flex items-center gap-2">
          <Image className="w-4 h-4 text-blue-600" />
          <span className="font-medium text-gray-800 text-sm">Question Image</span>
        </div>
      </div>

      {/* Image - Always visible for reference while solving */}
      {imageUrl && (
        <div className="p-3">
          <img
            src={imageUrl}
            alt="Question diagram"
            className="w-full rounded-lg border border-gray-200 max-h-80 object-contain bg-white"
          />
        </div>
      )}

      {/* Extracted Text - Short summary */}
      {extractedText && (
        <div className="px-3 pb-2">
          <p className="text-xs text-gray-600 leading-relaxed line-clamp-2">
            {extractedText}
          </p>
        </div>
      )}

      {hasRequiredInfo && (
        <div className="px-3 pb-3">
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-2">
            <div className="text-xs font-medium text-gray-700 mb-1">Given (from image)</div>

            {points.length > 0 && (
              <div className="mb-2">
                <div className="text-[11px] font-medium text-gray-600 mb-1 flex items-center gap-1">
                  <MapPin className="w-3 h-3 text-blue-600" />
                  Points
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {visiblePoints.map((coord, i) => (
                    <span
                      key={i}
                      className="text-xs bg-white px-2 py-1 rounded border border-blue-100 text-gray-700"
                    >
                      <strong className="text-blue-700">{coord.label}</strong> ({coord.x}, {coord.y})
                    </span>
                  ))}
                  {hiddenPointsCount > 0 && (
                    <span className="text-xs bg-white px-2 py-1 rounded border border-blue-100 text-gray-600">
                      +{hiddenPointsCount} more
                    </span>
                  )}
                </div>
              </div>
            )}

            {(angles.length > 0 || lengths.length > 0 || areas.length > 0 || other.length > 0) && (
              <div className="mb-2">
                <div className="text-[11px] font-medium text-gray-600 mb-1 flex items-center gap-1">
                  <Ruler className="w-3 h-3 text-blue-600" />
                  Values
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {visibleAngles.map((m, i) => (
                    <span
                      key={`a-${i}`}
                      className="text-xs bg-white px-2 py-1 rounded border border-blue-100 text-gray-700"
                    >
                      {m.name} = <strong className="text-blue-700">{m.value}</strong>
                    </span>
                  ))}
                  {hiddenAnglesCount > 0 && (
                    <span className="text-xs bg-white px-2 py-1 rounded border border-blue-100 text-gray-600">
                      +{hiddenAnglesCount} more
                    </span>
                  )}
                  {visibleLengths.map((m, i) => (
                    <span
                      key={`l-${i}`}
                      className="text-xs bg-white px-2 py-1 rounded border border-blue-100 text-gray-700"
                    >
                      {m.name} = <strong className="text-blue-700">{m.value}</strong>
                    </span>
                  ))}
                  {hiddenLengthsCount > 0 && (
                    <span className="text-xs bg-white px-2 py-1 rounded border border-blue-100 text-gray-600">
                      +{hiddenLengthsCount} more
                    </span>
                  )}
                  {visibleAreas.map((m, i) => (
                    <span
                      key={`ar-${i}`}
                      className="text-xs bg-white px-2 py-1 rounded border border-blue-100 text-gray-700"
                    >
                      {m.name} = <strong className="text-blue-700">{m.value}</strong>
                    </span>
                  ))}
                  {hiddenAreasCount > 0 && (
                    <span className="text-xs bg-white px-2 py-1 rounded border border-blue-100 text-gray-600">
                      +{hiddenAreasCount} more
                    </span>
                  )}
                  {visibleOther.map((m, i) => (
                    <span
                      key={`o-${i}`}
                      className="text-xs bg-white px-2 py-1 rounded border border-blue-100 text-gray-700"
                    >
                      {m.name} = <strong className="text-blue-700">{m.value}</strong>
                    </span>
                  ))}
                  {hiddenOtherCount > 0 && (
                    <span className="text-xs bg-white px-2 py-1 rounded border border-blue-100 text-gray-600">
                      +{hiddenOtherCount} more
                    </span>
                  )}
                </div>
              </div>
            )}

            {markings.length > 0 && (
              <div>
                <div className="text-[11px] font-medium text-gray-600 mb-1">Key markings</div>
                <ul className="text-xs text-gray-700 space-y-0.5">
                  {visibleMarkings.map((marking, i) => (
                    <li key={i} className="flex items-start gap-1.5">
                      <span className="text-blue-600 mt-0.5">•</span>
                      <span>{marking}</span>
                    </li>
                  ))}
                  {hiddenMarkingsCount > 0 && (
                    <li className="text-gray-600">+{hiddenMarkingsCount} more</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {hasDetails && (
        <div className="px-3 pb-3">
          <button
            type="button"
            onClick={() => setIsDetailsOpen(true)}
            className="w-full px-3 py-2 bg-blue-50 hover:bg-blue-100 transition-colors rounded-lg border border-blue-100 text-sm font-medium text-blue-700"
          >
            Open details
          </button>
        </div>
      )}

      {isDetailsOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/30"
          onClick={() => setIsDetailsOpen(false)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="absolute inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-white w-full sm:max-w-2xl sm:rounded-xl sm:border sm:border-gray-200 sm:shadow-lg max-h-[85vh] sm:max-h-[80vh] overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between bg-blue-50">
                <div className="text-sm font-medium text-gray-800">Question details</div>
                <button
                  type="button"
                  onClick={() => setIsDetailsOpen(false)}
                  className="p-1 rounded hover:bg-blue-100"
                  aria-label="Close"
                >
                  <X className="w-4 h-4 text-gray-700" />
                </button>
              </div>

              <div className="p-4 space-y-4 overflow-y-auto max-h-[calc(85vh-52px)] sm:max-h-[calc(80vh-52px)]">
                {imageUrl && (
                  <img
                    src={imageUrl}
                    alt="Question diagram"
                    className="w-full rounded-lg border border-gray-200 max-h-96 object-contain bg-white"
                  />
                )}

                {visualElements.diagramDescription && (
                  <div>
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Description</div>
                    <div className="text-sm text-gray-700 leading-relaxed">{visualElements.diagramDescription}</div>
                  </div>
                )}

                {extractedText && (
                  <div>
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Question text</div>
                    <div className="text-sm text-gray-700 leading-relaxed">{extractedText}</div>
                  </div>
                )}

                {(points.length > 0 || angles.length > 0 || lengths.length > 0 || areas.length > 0 || other.length > 0 || markings.length > 0) && (
                  <div>
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Given (from image)</div>

                    {points.length > 0 && (
                      <div className="mb-3">
                        <div className="text-[11px] font-medium text-gray-600 mb-1 flex items-center gap-1">
                          <MapPin className="w-3 h-3 text-blue-600" />
                          Points
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {points.map((coord, i) => (
                            <span
                              key={i}
                              className="text-xs bg-blue-50 px-2 py-1 rounded border border-blue-100 text-gray-700"
                            >
                              <strong className="text-blue-700">{coord.label}</strong> ({coord.x}, {coord.y})
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {(angles.length > 0 || lengths.length > 0 || areas.length > 0 || other.length > 0) && (
                      <div className="mb-3">
                        <div className="text-[11px] font-medium text-gray-600 mb-1 flex items-center gap-1">
                          <Ruler className="w-3 h-3 text-blue-600" />
                          Values
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {angles.map((m, i) => (
                            <span
                              key={`a-${i}`}
                              className="text-xs bg-blue-50 px-2 py-1 rounded border border-blue-100 text-gray-700"
                            >
                              {m.name} = <strong className="text-blue-700">{m.value}</strong>
                            </span>
                          ))}
                          {lengths.map((m, i) => (
                            <span
                              key={`l-${i}`}
                              className="text-xs bg-blue-50 px-2 py-1 rounded border border-blue-100 text-gray-700"
                            >
                              {m.name} = <strong className="text-blue-700">{m.value}</strong>
                            </span>
                          ))}
                          {areas.map((m, i) => (
                            <span
                              key={`ar-${i}`}
                              className="text-xs bg-blue-50 px-2 py-1 rounded border border-blue-100 text-gray-700"
                            >
                              {m.name} = <strong className="text-blue-700">{m.value}</strong>
                            </span>
                          ))}
                          {other.map((m, i) => (
                            <span
                              key={`o-${i}`}
                              className="text-xs bg-blue-50 px-2 py-1 rounded border border-blue-100 text-gray-700"
                            >
                              {m.name} = <strong className="text-blue-700">{m.value}</strong>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {markings.length > 0 && (
                      <div>
                        <div className="text-[11px] font-medium text-gray-600 mb-1">Key markings</div>
                        <ul className="text-sm text-gray-700 space-y-1">
                          {markings.map((marking, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <span className="text-blue-600 mt-1">•</span>
                              <span>{marking}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {visualElements.hasTable && visualElements.tableData?.headers?.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                      <Table className="w-3 h-3" />
                      Table
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border border-gray-200 rounded">
                        <thead>
                          <tr className="bg-gray-50">
                            {visualElements.tableData.headers.map((header, i) => (
                              <th key={i} className="px-2 py-1.5 text-left text-gray-700 font-medium border-b border-gray-200 text-xs">
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {visualElements.tableData.rows.map((row, i) => (
                            <tr key={i} className="border-b border-gray-100 last:border-0">
                              {row.map((cell, j) => (
                                <td key={j} className="px-2 py-1.5 text-gray-600 text-xs">
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {visualElements.hasGraph && visualElements.graphDescription && (
                  <div>
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Graph</div>
                    <div className="text-sm text-gray-700 leading-relaxed">{visualElements.graphDescription}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiagramCard;
